import { cs } from "../utils/currency";
import React, { useEffect, useState } from "react";
import {
  getDeliveryQuote,
  updateOrderDelivery,
  updateOrderDeliveryExternal,
} from "../services/api";
import DoorDash from "../assests/DASH.svg";
import Uber from "../assests/uber-icon.svg";

type DeliveryProvider = "DoorDash" | "Uber" | "Custom Delivery";

interface DeliveryOrder {
  _id: string;
  metadata?: Record<string, any>;
  items?: any[];
  tipAmount?: number;
  userId?: string;
  orderBy?: string;
}

interface Props {
  order: DeliveryOrder;
  totalAmount: number;
  onClose: () => void;
  onAssigned: (orderId: string, provider: DeliveryProvider) => void;
}

const formatQuoteFee = (fee?: number, currency?: string) => {
  if (typeof fee !== "number") return "N/A";
  const amount = (fee / 100).toFixed(2);
  const code = (currency || "").toUpperCase();
  return code ? `${code} ${amount}` : `${cs()}${amount}`;
};

const getQuoteDeliveryId = (data?: any) =>
  data?.deliveryId ||
  data?.delivery_id ||
  data?.id ||
  data?.delivery?.id ||
  data?.quoteId ||
  data?.quote_id;

const providerButtonClass =
  "flex items-center justify-between gap-3 px-3 py-2 rounded-full text-xs font-semibold border border-gray-200 text-gray-700 disabled:opacity-40";

/**
 * Choose who delivers an order: quotes Uber and DoorDash for its address,
 * books the one picked, or keeps it as the store's own Custom Delivery.
 */
const DeliveryProvidersModal: React.FC<Props> = ({
  order,
  totalAmount,
  onClose,
  onAssigned,
}) => {
  const [quoteLoading, setQuoteLoading] = useState(true);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quoteData, setQuoteData] = useState<any>(null);
  // Blocks a second click while a provider is being booked
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const dropoff_address = order.metadata?.dropoff_address;
    const dropoff_latitude = Number(order.metadata?.dropoff_latitude);
    const dropoff_longitude = Number(order.metadata?.dropoff_longitude);
    const pickup_time =
      typeof order.metadata?.pickup_time === "string" &&
      order.metadata.pickup_time.trim().length > 0
        ? order.metadata.pickup_time
        : new Date().toISOString();

    if (
      !dropoff_address ||
      Number.isNaN(dropoff_latitude) ||
      Number.isNaN(dropoff_longitude)
    ) {
      setQuoteError("Missing dropoff details for quote.");
      setQuoteLoading(false);
      return;
    }

    getDeliveryQuote({
      dropoff_address,
      dropoff_latitude,
      dropoff_longitude,
      pickup_time,
    })
      .then((res) => {
        if (!cancelled) setQuoteData(res.data);
      })
      .catch((error) => {
        console.error("Failed to fetch delivery quote", error);
        if (!cancelled) setQuoteError("Failed to fetch delivery quote.");
      })
      .finally(() => {
        if (!cancelled) setQuoteLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [order]);

  const uberQuote = quoteData?.quotes?.uber;
  const doordashQuote = quoteData?.quotes?.doordash;

  const bookProvider = async (provider: "DoorDash" | "Uber") => {
    const quote = provider === "Uber" ? uberQuote : doordashQuote;
    const deliveryId = getQuoteDeliveryId(quote?.data);
    if (!deliveryId) {
      setQuoteError(`Missing ${provider} delivery ID from quote.`);
      return;
    }
    setAssigning(true);
    try {
      await updateOrderDeliveryExternal({
        orderId: order._id,
        platform: provider,
        deliveryId,
        tipAmount: order.tipAmount,
        metadata: order.metadata,
        items: order.items,
        totalAmount,
        userId: order.userId,
        orderBy: order.orderBy,
      });
      onAssigned(order._id, provider);
      onClose();
    } catch (error) {
      console.error(`Failed to update ${provider} delivery`, error);
      setQuoteError(`Could not book ${provider}. Please try again.`);
      setAssigning(false);
    }
  };

  const assignCustomDelivery = async () => {
    setAssigning(true);
    try {
      await updateOrderDelivery(order._id, "Custom Delivery");
      onAssigned(order._id, "Custom Delivery");
      onClose();
    } catch (error) {
      console.error("Failed to assign delivery provider", error);
      setQuoteError("Could not update the delivery.");
      setAssigning(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center px-4"
      onClick={() => !assigning && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-lg w-full max-w-sm p-4"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="text-black font-outfit font-semibold text-lg">
          Delivery Providers
        </div>

        <div className="mt-4">
          {quoteLoading ? (
            <p className="text-gray-500 text-sm">Loading delivery quotes...</p>
          ) : (
            <div className="flex flex-col gap-3">
              {quoteError && <p className="text-red-500 text-sm m-0">{quoteError}</p>}
              <button
                type="button"
                onClick={() => bookProvider("DoorDash")}
                disabled={!doordashQuote?.success || assigning}
                className={providerButtonClass}
              >
                <span className="flex items-center gap-2">
                  <img src={DoorDash} alt="DoorDash" className="w-5 h-5" />
                  DoorDash
                </span>
                <span className="text-[11px] text-gray-500">
                  {doordashQuote?.success
                    ? formatQuoteFee(
                        doordashQuote.data?.fee,
                        doordashQuote.data?.currency ||
                          doordashQuote.data?.currency_type,
                      )
                    : "Unavailable"}
                </span>
              </button>
              <button
                type="button"
                onClick={() => bookProvider("Uber")}
                disabled={!uberQuote?.success || assigning}
                className={providerButtonClass}
              >
                <span className="flex items-center gap-2">
                  <img src={Uber} alt="Uber" className="w-5 h-5" />
                  Uber
                </span>
                <span className="text-[11px] text-gray-500">
                  {uberQuote?.success
                    ? formatQuoteFee(
                        uberQuote.data?.fee,
                        uberQuote.data?.currency ||
                          uberQuote.data?.currency_type,
                      )
                    : "Unavailable"}
                </span>
              </button>
              <button
                type="button"
                onClick={assignCustomDelivery}
                disabled={assigning}
                className={providerButtonClass}
              >
                <span>Self Deliver</span>
              </button>
            </div>
          )}
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            className="px-3 py-2 rounded-md border border-gray-300 text-sm font-outfit disabled:opacity-40"
            onClick={onClose}
            disabled={assigning}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeliveryProvidersModal;
